import React, {Component, useEffect, useRef} from 'react'
import Chart from 'chart.js/auto'
import annotationPlugin from 'chartjs-plugin-annotation'
import {getProp} from '../../../../common/util/util-helpers'
import {toFrontDateNoTime} from '../../../../common/util/util-dates'
import {getInfoResource} from "../../../../data/actions/infoResource"
import LocalStorage from "../../../../util/localStorage"
import Resources from "../../../../data/services/resources"
import ModalDefault from "../../../../common/components/modal/modal-default"
import {includeFields} from "../../../../common/util/util-fields"
import PopOver from "../../../../common/components/popover"
import ChevronDownIcon from "@heroicons/react/20/solid/ChevronDownIcon"
import {LoaderSmall} from "../../../../common/components/loader"

Chart.register(annotationPlugin)

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

const StatCard = ({title, value, accent}) => (
    <div className="bg-popup border border-tm-gray-300 rounded-card p-4">
        <p className="text-xs font-medium text-tm-gray-500 uppercase tracking-wide">{title}</p>
        <p className={`text-lg font-bold mt-1 tabular-nums ${accent || 'text-tm-gray-900'}`}>{value}</p>
    </div>
)

const TimeSeriesLineChart = ({points}) => {
    const canvasRef = useRef(null)
    const chartRef = useRef(null)

    const styles = typeof document !== 'undefined' ? getComputedStyle(document.documentElement) : null
    const primaryColor = styles?.getPropertyValue('--color-primary').trim() || '#4f46e5'

    useEffect(() => {
        if (!canvasRef.current || !points.length) return
        if (chartRef.current) chartRef.current.destroy()

        const ctx = canvasRef.current.getContext('2d')
        const avg = points.reduce((s, d) => s + d.value, 0) / points.length

        chartRef.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels: points.map(d => d.label),
                datasets: [{
                    data: points.map(d => d.value),
                    borderColor: primaryColor,
                    backgroundColor: primaryColor + '14',
                    borderWidth: 2.5,
                    pointRadius: 3,
                    pointBackgroundColor: 'transparent',
                    pointBorderColor: primaryColor,
                    pointBorderWidth: 2,
                    pointHoverRadius: 6,
                    tension: 0.3,
                    fill: true,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {intersect: false, mode: 'index'},
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#111827',
                        bodyColor: '#4b5563',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        displayColors: false,
                        callbacks: {
                            label: (item) => fmt$(item.raw),
                        },
                    },
                    annotation: {
                        annotations: {
                            avgLine: {
                                type: 'line',
                                yMin: avg,
                                yMax: avg,
                                borderColor: '#9ca3af',
                                borderWidth: 1,
                                borderDash: [4, 4],
                                label: {
                                    display: true,
                                    content: `Avg ${fmt$(avg)}`,
                                    position: 'end',
                                    backgroundColor: 'transparent',
                                    color: '#9ca3af',
                                    font: {size: 11},
                                },
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {display: false},
                        ticks: {color: '#6b7280', font: {size: 11}},
                        border: {color: '#e5e7eb'},
                    },
                    y: {
                        grid: {color: '#f3f4f6'},
                        ticks: {color: '#6b7280', font: {size: 11}, callback: (v) => `$${v.toLocaleString()}`},
                        border: {display: false},
                    },
                },
            },
        })

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy()
                chartRef.current = null
            }
        }
    }, [points])

    if (!points.length) {
        return <div className="flex items-center justify-center h-full text-sm text-tm-gray-400">No data</div>
    }

    return <canvas ref={canvasRef}/>
}

export default class AccountingLineChartDialog extends Component {

    constructor(props) {
        super(props)
        this.state = {}
    }

    /** Lifecycle
     ================================================================= */
    componentDidUpdate(prevProps) {
        if (prevProps.show !== this.props.show && !!this.props.show) {
            this.fetchData()
        }

        if (this.props.filters !== prevProps.filters) {
            if (this.props.show) {
                this.fetchData()
            }
        }
    }

    /** Data Events
     ================================================================= */
    fetchData = () => {
        this.props.dispatch(getInfoResource({
            user: LocalStorage.get('user'),
            query: Object.assign({}, this.props.query, {PerDate: 1}),
            resource: Resources.AccountingAccountReport
        }))
    }

    /** Render
     ================================================================= */
    render() {
        const {translate, info} = this.props

        const isLoading = getProp(info, 'isLoading', false)
        const rawData = getProp(info, 'data', [])

        const points = Array.isArray(rawData)
            ? rawData.map(it => ({label: toFrontDateNoTime(it[0]), value: Number(it[1]) || 0}))
            : []

        const values = points.map(p => p.value)
        const avg = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0
        const high = values.length ? Math.max(...values) : 0
        const low = values.length ? Math.min(...values) : 0
        const current = values.length ? values[values.length - 1] : 0

        const startDateField = includeFields(Object.assign({}, this.props.filters), ['StartDate', 'EndDate'])
        const startDate = startDateField?.StartDate?.value
        const endDate = startDateField?.EndDate?.value
        const dateLabel = startDate && endDate
            ? `${toFrontDateNoTime(startDate)} – ${toFrontDateNoTime(endDate)}`
            : ''

        return (
            <ModalDefault
                show={this.props.show}
                widthClass="max-w-[90rem]"
                title={this.props.title ?? ''}
                closeButtonLabel={translate('btn.Close')}
                onClose={this.props.onClose}
                afterTitle={
                    <div className="flex items-center gap-3 ml-4 mb-3 mr-10">
                        {dateLabel && (
                            <span className="text-xs text-tm-gray-400">{dateLabel}</span>
                        )}
                        <PopOver
                            className="block"
                            btnClass="form-control p-0 w-auto text-tm-gray-700 flex items-center"
                            widthClass="max-w-2xl"
                            positionClass="absolute"
                            btnCustomHtml={(
                                <React.Fragment>
                                    <div className="py-2 px-3">
                                        <span className="relative text-sm">Filters</span>
                                    </div>
                                    <span className="h-5 w-px bg-tm-gray-200"/>
                                    <div className="py-1 px-2">
                                        <ChevronDownIcon className="w-5 h-5"/>
                                    </div>
                                </React.Fragment>
                            )}
                        >
                            <div className="bg-popup p-6 border border-tm-gray-300 rounded-lg">
                                {this.props.children}
                            </div>
                        </PopOver>
                    </div>
                }
            >
                {isLoading && (
                    <div className="flex items-center justify-center py-20">
                        <LoaderSmall/>
                    </div>
                )}

                {!isLoading && (
                    <div className="p-6 space-y-6">
                        {/* Stat cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <StatCard title="Current" value={fmt$(current)}/>
                            <StatCard title="Average" value={fmt$(avg)}/>
                            <StatCard title="High" value={fmt$(high)} accent="text-green-600"/>
                            <StatCard title="Low" value={fmt$(low)} accent="text-red-500"/>
                        </div>

                        {/* Chart */}
                        <div>
                            <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide mb-2">
                                Balance over time
                            </p>
                            <div className="bg-popup border border-tm-gray-300 rounded-card p-4"
                                 style={{height: 'calc(100vh - 26rem)'}}>
                                <TimeSeriesLineChart points={points}/>
                            </div>
                        </div>
                    </div>
                )}
            </ModalDefault>
        )
    }
}
